/**
 * GymLog: shared Drizzle query builders (PowerSync, local SQLite).
 *
 * Each builder is pure (db + params → query) and consumed by hooks in
 * `solid/use-gym-queries.ts` with canonical queryKeys from the factory.
 */

import { and, asc, desc, eq, gte, isNotNull, isNull, or, sql } from "drizzle-orm";
import {
  body_measurement,
  exercise,
  type PowerSyncDrizzleDB,
  routine,
  routine_exercise,
  routine_version,
  workout_session,
  workout_set,
} from "@gymlog/powersync";
import { EXERCISE_HISTORY_LIMIT } from "./constants";
import { liveExerciseFilters, liveRoutineFilters } from "../live/filters";

// ─── Exercises ───────────────────────────────────────────────────────────────

/**
 * Catalog visible to user: global (user_id NULL) + custom.
 * Archived entries are excluded unless `includeArchived`.
 */
export function getExercisesQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  options: { includeArchived?: boolean } = {},
) {
  const visibility = or(isNull(exercise.user_id), eq(exercise.user_id, userId));
  // Trash (L2): invariant `deleted_at IS NULL` lives in live layer
  // (liveExerciseFilters). Global catalog (user_id NULL) is never deleted.
  const alive = and(visibility, ...liveExerciseFilters());
  const filters = options.includeArchived
    ? alive
    : and(alive, sql`COALESCE(${exercise.is_archived}, 0) = 0`);

  return db.select().from(exercise).where(filters).orderBy(asc(exercise.name_en));
}

/** Trash: custom exercises with soft-delete (recent first). */
export function getTrashedExercisesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(exercise)
    .where(and(eq(exercise.user_id, userId), isNotNull(exercise.deleted_at)))
    .orderBy(desc(exercise.deleted_at));
}

// ─── Routines ──────────────────────────────────────────────────────────────────

/** User routines with exercise count (sidebar + home). */
export function getRoutinesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      id: routine.id,
      user_id: routine.user_id,
      name: routine.name,
      description: routine.description,
      position: routine.position,
      is_archived: routine.is_archived,
      deleted_at: routine.deleted_at,
      created_at: routine.created_at,
      updated_at: routine.updated_at,
      // ROUTINE VERSIONING: current pointers (required to match
      // full routine row type after adding these columns).
      current_version_id: routine.current_version_id,
      version_number: routine.version_number,
      exercise_count: sql<number>`count(${routine_exercise.id})`.mapWith(Number),
    })
    .from(routine)
    .leftJoin(routine_exercise, eq(routine_exercise.routine_id, routine.id))
    .where(
      and(
        eq(routine.user_id, userId),
        ...liveRoutineFilters(),
        sql`COALESCE(${routine.is_archived}, 0) = 0`,
      ),
    )
    .groupBy(routine.id)
    .orderBy(asc(routine.position), asc(routine.created_at));
}

/** Trash: routines with soft-delete (recent first). */
export function getTrashedRoutinesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(routine)
    .where(and(eq(routine.user_id, userId), isNotNull(routine.deleted_at)))
    .orderBy(desc(routine.deleted_at));
}

/** A single routine by id. */
export function getRoutineDetailQuery(db: PowerSyncDrizzleDB, routineId: string) {
  return db.select().from(routine).where(eq(routine.id, routineId)).limit(1);
}

/** Routine exercises in order, with exercise metadata. */
export function getRoutineExercisesQuery(db: PowerSyncDrizzleDB, routineId: string) {
  return (
    db
      .select({
        id: routine_exercise.id,
        routine_id: routine_exercise.routine_id,
        exercise_id: routine_exercise.exercise_id,
        position: routine_exercise.position,
        target_sets: routine_exercise.target_sets,
        target_reps_min: routine_exercise.target_reps_min,
        target_reps_max: routine_exercise.target_reps_max,
        rest_seconds: routine_exercise.rest_seconds,
        note: routine_exercise.note,
        name_en: exercise.name_en,
        name_es: exercise.name_es,
        muscle_group: exercise.muscle_group,
        equipment: exercise.equipment,
        default_rest_seconds: exercise.default_rest_seconds,
      })
      .from(routine_exercise)
      .innerJoin(exercise, eq(exercise.id, routine_exercise.exercise_id))
      // A trashed exercise must not continue appearing in routines
      // (invariant in live layer).
      .where(and(eq(routine_exercise.routine_id, routineId), ...liveExerciseFilters()))
      .orderBy(asc(routine_exercise.position), asc(routine_exercise.created_at))
  );
}

/**
 * LOCAL version history of a routine (ROUTINE VERSIONING) — PowerSync
 * bucket bounded to sync window (last 10). UUIDv7 id ⇒ descending
 * order = newest first. Deep history is requested via API
 * (GET /api/gym/routine/:id/versions).
 */
export function getRoutineVersionsQuery(db: PowerSyncDrizzleDB, routineId: string) {
  return db
    .select()
    .from(routine_version)
    .where(eq(routine_version.routine_id, routineId))
    .orderBy(desc(routine_version.id));
}

// ─── Sessions ─────────────────────────────────────────────────────────────────

/** Most recent open session (without finished_at) of the user. */
export function getOpenSessionQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(workout_session)
    .where(and(eq(workout_session.user_id, userId), isNull(workout_session.finished_at)))
    .orderBy(desc(workout_session.started_at))
    .limit(1);
}

/** History of completed sessions, newest first. */
export function getSessionsQuery(db: PowerSyncDrizzleDB, userId: string, limit: number) {
  return db
    .select({
      id: workout_session.id,
      routine_id: workout_session.routine_id,
      name: workout_session.name,
      started_at: workout_session.started_at,
      finished_at: workout_session.finished_at,
      note: workout_session.note,
      set_count: sql<number>`count(${workout_set.id})`.mapWith(Number),
      volume_kg:
        sql<number>`COALESCE(sum(CASE WHEN COALESCE(${workout_set.is_warmup},0)=0 THEN ${workout_set.weight_kg} * ${workout_set.reps} ELSE 0 END), 0)`.mapWith(
          Number,
        ),
    })
    .from(workout_session)
    .leftJoin(workout_set, eq(workout_set.session_id, workout_session.id))
    .where(eq(workout_session.user_id, userId))
    .groupBy(workout_session.id)
    .orderBy(desc(workout_session.started_at))
    .limit(limit);
}

/** A single session by id. */
export function getSessionDetailQuery(db: PowerSyncDrizzleDB, sessionId: string) {
  return db.select().from(workout_session).where(eq(workout_session.id, sessionId)).limit(1);
}

/** Sets of a session with exercise metadata, in logging order. */
export function getSessionSetsQuery(db: PowerSyncDrizzleDB, sessionId: string) {
  return db
    .select({
      id: workout_set.id,
      session_id: workout_set.session_id,
      exercise_id: workout_set.exercise_id,
      set_number: workout_set.set_number,
      weight_kg: workout_set.weight_kg,
      reps: workout_set.reps,
      rpe: workout_set.rpe,
      is_warmup: workout_set.is_warmup,
      completed_at: workout_set.completed_at,
      name_en: exercise.name_en,
      name_es: exercise.name_es,
      muscle_group: exercise.muscle_group,
    })
    .from(workout_set)
    .innerJoin(exercise, eq(exercise.id, workout_set.exercise_id))
    .where(eq(workout_set.session_id, sessionId))
    .orderBy(asc(workout_set.completed_at), asc(workout_set.set_number));
}

// ─── History / PRs / preload ─────────────────────────────────────────────────

/**
 * Complete history of working sets + warmups for ONE exercise (PRs, preload
 * and charts are derived from here with pure logic in `logic.ts`).
 */
export function getExerciseHistoryQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  exerciseId: string,
) {
  return db
    .select()
    .from(workout_set)
    .where(and(eq(workout_set.user_id, userId), eq(workout_set.exercise_id, exerciseId)))
    .orderBy(asc(workout_set.completed_at))
    .limit(EXERCISE_HISTORY_LIMIT);
}

// ─── Weekly summary ──────────────────────────────────────────────────────────

/** Sets since a date (ISO) with exercise muscle group. */
export function getSetsSinceQuery(db: PowerSyncDrizzleDB, userId: string, sinceISO: string) {
  return (
    db
      .select({
        id: workout_set.id,
        session_id: workout_set.session_id,
        exercise_id: workout_set.exercise_id,
        weight_kg: workout_set.weight_kg,
        reps: workout_set.reps,
        is_warmup: workout_set.is_warmup,
        completed_at: workout_set.completed_at,
        muscle_group: exercise.muscle_group,
      })
      .from(workout_set)
      .innerJoin(exercise, eq(exercise.id, workout_set.exercise_id))
      // Sets of a trashed exercise are excluded from weekly summary
      // (clean derived stats, via live layer); reappear upon restore.
      .where(
        and(
          eq(workout_set.user_id, userId),
          gte(workout_set.completed_at, sinceISO),
          ...liveExerciseFilters(),
        ),
      )
      .orderBy(asc(workout_set.completed_at))
  );
}

// ─── Body measurements ──────────────────────────────────────────────────────

/** User body measurements, newest first. */
export function getBodyMeasurementsQuery(db: PowerSyncDrizzleDB, userId: string, limit?: number) {
  const query = db
    .select()
    .from(body_measurement)
    .where(eq(body_measurement.user_id, userId))
    .orderBy(desc(body_measurement.measured_at));

  if (limit !== undefined) {
    return query.limit(limit);
  }
  return query;
}
